/* eslint-disable no-underscore-dangle */
import { t } from '@lingui/macro';
import classnames from 'classnames';
import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { i18n } from '@lingui/core';
import { onChangeParkingFilter, onChangeParkingCapacitySizing } from '../../../ac/onChangeMapFilters';
import { ReactComponent as CircleLegend } from '../../../assets/images/circle.svg';
import { parkingTypes } from '../../../constants/variables';
import { WHOOSH_PARKING_CAPACITY_LEGEND, WHOOSH_HOTSPOT_LEGEND } from '../../../constants/legends';
import { WHOOSH_PARKING_TYPE_STYLE } from '../../../constants/layerStyles';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import styles from '../FilterSidebar.module.css';
import { fetchParkingId } from '../../../utils/fetchParkingId';
import LegendRenderer from '../../LegendRenderer/LegendRenderer';

const ParkingFiltersComponent = ({
    mapFilters,
    onChangeParkingFilter,
}) => {
    const [ searchValue, setSearchValue ] = useState('');
    const [ searchResult, setSearchResult ] = useState(null);
    const [ searchError, setSearchError ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const handleSearch = async e => {
        e.preventDefault();
        setIsLoading(true);
        setSearchError('');
        setSearchResult(null);
        try {
            const result = await fetchParkingId(searchValue.trim());
            if (result) {
                setSearchResult(result);
                setSearchError('');
                // Переместить карту к парковке
                // eslint-disable-next-line no-underscore-dangle
                if (window.__map && result.lat && result.lon) {
                    window.__map.flyTo({ center: [ result.lon, result.lat ], zoom: 17 });
                }
            } else {
                setSearchError(t`Парковка не найдена`);
            }
        } catch (err) {
            setSearchError(t`Ошибка поиска`);
        } finally {
            setIsLoading(false);
        }
    };
    const clearSearch = () => {
        setSearchValue('');
        setSearchResult(null);
        setSearchError('');
    };

    return (
        <div>
            {/* Поиск парковки */}
            <form
                onSubmit={handleSearch}
                style={{ padding: '0 8px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                        <input
                            type="text"
                            placeholder={t`Parking ID или Internal ID`}
                            value={searchValue}
                            onChange={e => setSearchValue(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                paddingRight: searchValue ? '32px' : '12px',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                fontSize: '16px',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => e.target.style.borderColor = '#007bff'}
                            onBlur={e => e.target.style.borderColor = '#ccc'}
                        />
                        {searchValue && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    color: '#999',
                                    padding: '0',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onMouseEnter={e => e.target.style.color = '#666'}
                                onMouseLeave={e => e.target.style.color = '#999'}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading || !searchValue.trim()}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: isLoading || !searchValue.trim() ? '#6c757d' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: isLoading || !searchValue.trim() ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s ease',
                            width: 'fit-content',
                        }}
                        onMouseEnter={e => {
                            if (!isLoading && searchValue.trim()) {
                                e.target.style.backgroundColor = '#0056b3';
                            }
                        }}
                        onMouseLeave={e => {
                            if (!isLoading && searchValue.trim()) {
                                e.target.style.backgroundColor = '#007bff';
                            }
                        }}
                    >
                        {isLoading ? t`Поиск...` : t`Найти парковку`}
                    </button>
                </div>
                {searchError && (
                    <div
                        style={{
                            color: '#dc3545',
                            marginTop: 8,
                            fontSize: '14px',
                            padding: '8px 12px',
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                        }}
                    >
                        {searchError}
                    </div>
                )}
                {searchResult && !searchError && (
                    <div
                        style={{
                            color: '#155724',
                            marginTop: 8,
                            fontSize: '14px',
                            padding: '8px 12px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '4px',
                        }}
                    >
                        {t`Парковка (ID)`}: {searchResult.id}<br />{t`Статус`}: {searchResult.status}
                    </div>
                )}
            </form>
            {/* Существующий фильтр по статусу парковки */}
            <div className={styles.legHead}>{t`Статус парковки`}:</div>
            <div className={styles.checkboxIconGrid}>
                {
                    parkingTypes.map(({ value, color }) => (
                        <Fragment key={value}>
                            <CircleLegend
                                fill={color}
                                width='2vw'
                                height='2vw'
                                opacity='0.8'
                            />
                            <label htmlFor={value}>{value}</label>
                            <div>
                                <label className={styles.switch}>
                                    <input
                                        type='checkbox'
                                        name='parkings'
                                        id={value}
                                        checked={mapFilters.parkingLotStatuses.includes(value)}
                                        value={value}
                                        onChange={onChangeParkingFilter}
                                    />
                                    <span className={classnames(styles.sliderRound, styles.slider)} />
                                </label>
                            </div>
                        </Fragment>
                    ))
                }
            </div>
        </div>
    );
};
// Легенда вместимости с тоглом: выключенный тогл убирает размерную градуировку
// из легенды (и, как следствие, из экспорта карты в растр) — сам переключатель
// помечен data-export-omit как контрол.
const ParkingCapacitySizingComponent = ({ parkingCapacitySizing, onChangeParkingCapacitySizing }) => (
    <div style={{ maxWidth: '300px' }}>
        <div
            className={styles.legHead}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
            {i18n._(WHOOSH_PARKING_CAPACITY_LEGEND.title)}
            <label
                className={styles.switch}
                data-export-omit=""
            >
                <input
                    type='checkbox'
                    name='parkingCapacitySizing'
                    checked={parkingCapacitySizing}
                    onChange={onChangeParkingCapacitySizing}
                />
                <span className={classnames(styles.sliderRound, styles.slider)} />
            </label>
        </div>
        {parkingCapacitySizing && (
            <LegendRenderer config={{ ...WHOOSH_PARKING_CAPACITY_LEGEND, title: null }} />
        )}
    </div>
);
const ConnectedParkingCapacityLegend = connect(
    ({ mapFilters: { parkingCapacitySizing } }) => ({ parkingCapacitySizing }),
    { onChangeParkingCapacitySizing }
)(ParkingCapacitySizingComponent);
const HotspotLegendComponent = () => (
    <LegendRenderer config={WHOOSH_HOTSPOT_LEGEND} />
);
const ParkingTypeLegendComponent = () => (
    <div style={{ maxWidth: '300px' }}>
        <div className={styles.legHead}>{t`Тип парковки`}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr', columnGap: '12px', rowGap: '8px', alignItems: 'center', paddingLeft: '10px' }}>
            {WHOOSH_PARKING_TYPE_STYLE.types.map(item => (
                <Fragment key={item.id}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                        >
                            <circle
                                cx="7"
                                cy="7"
                                r={6 - item.strokeWidth / 2}
                                fill={item.color}
                                stroke={item.strokeColor}
                                strokeWidth={item.strokeWidth}
                                opacity="0.9"
                            />
                        </svg>
                    </div>
                    <p style={{ fontSize: '12px', margin: '0', lineHeight: '1.4' }}>
                        {i18n._(item.label)}
                    </p>
                </Fragment>
            ))}
        </div>
    </div>
);
const mapStateToProps = ({ mapFilters }) => ({ mapFilters });
const ConnectedComponent = connect(mapStateToProps, { onChangeParkingFilter })(ParkingFiltersComponent);

export const ParkingFilters = withDisplayedLayersCheck(ConnectedComponent, t`Парковки whoosh`);
export const ParkingCapacityLegend = withDisplayedLayersCheck(ConnectedParkingCapacityLegend, t`Парковки whoosh`);
export const HotspotLegend = withDisplayedLayersCheck(HotspotLegendComponent, t`Парковки whoosh`);
export const ParkingTypeLegend = withDisplayedLayersCheck(ParkingTypeLegendComponent, t`Парковки whoosh`);
